// Pure presentation helpers. Kept tiny and testable so the rendering
// components don't carry locale logic inline.

import type { NotificationState, PipelineCadence, PipelineEvent, RotationCycle } from "./types";

/**
 * Render an ISO-8601 instant as the Growdhi-canonical UI form,
 * e.g. `May 4, 2026 at 6:22 PM UTC`.
 *
 * Always rendered in UTC so two operators in different timezones see the
 * same string for the same event (this is the whole point of `ts_utc`).
 */
export function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso; // fall back to raw on parse failure
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: true,
  }).format(d);
  return `${datePart} at ${timePart} UTC`;
}

/**
 * Fallback delivery-cadence classification from a `pipeline` slug.
 *
 * Used only when the API did not resolve a live cadence for the row/source
 * (kind `unknown` or field absent - e.g. schedule unreadable, or an older
 * API). The authoritative cadence now comes from the source's live
 * EventBridge schedule (see `cadenceLabel` / `cadenceClass` below); this
 * heuristic just keeps the UI sensible when that resolution is unavailable.
 */
export type Cadence = "Near Real-time" | "Batch";

export function classifyCadence(pipeline: string): Cadence {
  if (pipeline === "flowing") return "Near Real-time";
  return "Batch";
}

/** True when the API resolved a concrete cadence (not absent, not unknown). */
function hasResolvedCadence(cadence: PipelineCadence | undefined): cadence is PipelineCadence {
  return !!cadence && cadence.kind !== "unknown";
}

/**
 * Human cadence label to display. Prefers the live schedule-derived label
 * ("Every 90 min", "Daily"); falls back to the slug category when the API
 * could not resolve a cadence.
 */
export function cadenceLabel(cadence: PipelineCadence | undefined, pipeline: string): string {
  if (hasResolvedCadence(cadence)) return cadence.label;
  return classifyCadence(pipeline);
}

/** CSS suffix ("realtime" | "batch") for the cadence chip colour. */
export function cadenceClass(
  cadence: PipelineCadence | undefined,
  pipeline: string,
): "realtime" | "batch" {
  if (hasResolvedCadence(cadence)) {
    return cadence.kind === "near_real_time" ? "realtime" : "batch";
  }
  return classifyCadence(pipeline) === "Near Real-time" ? "realtime" : "batch";
}

/** True when the effective cadence is near-real-time (for client staleness UX). */
export function isNearRealTime(cadence: PipelineCadence | undefined, pipeline: string): boolean {
  if (hasResolvedCadence(cadence)) return cadence.kind === "near_real_time";
  return classifyCadence(pipeline) === "Near Real-time";
}

/**
 * Pull a record count out of an event's metrics, when the producer
 * supplied one. Returns `null` when no count is present.
 *
 * Most aws.states-derived events (the v1 backbone) won't have one - only
 * producer-emitted growdhi.pipeline events with an explicit `record_count`
 * (or the legacy `records_processed` alias) will. We tolerate either key
 * so the FE doesn't break when a future producer chooses a different name;
 * the UI just shows whichever one it finds first.
 */
export function extractRecordCount(event: PipelineEvent): number | null {
  const m = event.metrics;
  if (!m || typeof m !== "object") return null;
  for (const key of ["record_count", "records_processed", "row_count"]) {
    const v = (m as Record<string, unknown>)[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Display-name formatters.
//
// Raw identifiers in the data layer are lowercase + snake_case (`magento`,
// `bq_ga4`, `apeak`, `order_items`). UI surfaces should render their human-
// readable form. Keep this transform on the FE so the API stays canonical.
// ---------------------------------------------------------------------------

// Acronyms preserved in their canonical case when they appear as a snake-case
// segment. Extend as new entities land - kept small so the helper stays trivial.
const ACRONYMS: Set<string> = new Set(["faq", "faqs", "id", "ids", "url", "urls", "sku", "skus"]);

function titleCaseWord(word: string): string {
  if (!word) return word;
  if (ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
  return word[0].toUpperCase() + word.slice(1).toLowerCase();
}

function titleCaseSnake(value: string): string {
  return value.split("_").map(titleCaseWord).join(" ");
}

/** Source-system display name (e.g. `bq_ga4` → `GA4 BigQuery`). */
const SOURCE_DISPLAY_NAMES: Record<string, string> = {
  magento: "Magento",
  shopify: "Shopify",
  bq_ga4: "GA4 BigQuery",
  klaviyo: "Klaviyo",
};

export function formatSource(source: string): string {
  if (!source) return source;
  return SOURCE_DISPLAY_NAMES[source] ?? titleCaseSnake(source);
}

/** Tenant display name (e.g. `apeak` → `APeak`). */
const TENANT_DISPLAY_NAMES: Record<string, string> = {
  apeak: "APeak",
  dak: "DAK",
  magedelight: "MageDelight",
};

export function formatTenant(tenant: string): string {
  if (!tenant) return tenant;
  return TENANT_DISPLAY_NAMES[tenant] ?? titleCaseWord(tenant);
}

/** Entity display name (e.g. `order_items` → `Order Items`, `category_faqs` → `Category FAQs`).
 *
 * Producer events emit snake_case identifiers; backend Glue-fallback summaries
 * emit pre-formatted display strings ("GA4 Events", "2 raw entities"). Pass
 * pre-formatted strings through unchanged so the snake-case transform does not
 * downcase acronyms like "GA4" into "Ga4".
 */
export function formatEntity(name: string): string {
  if (!name) return name;
  if (name.includes(" ")) return name;
  return titleCaseSnake(name);
}

/**
 * Rewrite the tenant prefix in a pre-formatted message string and
 * normalize stylistic em-dashes to plain ASCII hyphens.
 *
 * Producers emit messages like ``"apeak: GA4 Flowing Sync complete - N records ingested"``.
 * Historical messages (already stored in DDB) used the em-dash glyph; rendering
 * them through this helper rewrites them in-place so the UI stays consistent
 * with the canonical hyphen form regardless of when the producer last shipped.
 */
export function formatMessage(message: string, tenantId: string): string {
  if (!message) return message;
  const normalised = message.replace(/—/g, "-");
  if (!tenantId) return normalised;
  const prefix = tenantId.toLowerCase() + ":";
  if (normalised.toLowerCase().startsWith(prefix)) {
    return formatTenant(tenantId) + normalised.slice(tenantId.length);
  }
  return normalised;
}

// ---------------------------------------------------------------------------
// Relative-time + expiry helpers (added with the rotation/cooldown work).
// ---------------------------------------------------------------------------

/**
 * Render an ISO instant as a coarse "time since" string.
 *
 * Used for "Last sync" on Integration rows. Keeps the grain low (seconds
 * / minutes / hours / days) so a glance tells the operator whether the
 * connection is fresh - exact precision is not the point here.
 *
 * `null` / unparseable input -> "Never".
 */
export function formatRelativeTime(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const diffSec = Math.max(0, Math.round((nowMs - t) / 1000));
  if (diffSec < 60) return diffSec <= 5 ? "just now" : `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffSec / 3600);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffSec / 86400);
  if (diffDay < 30) return `${diffDay}d ago`;
  // Beyond a month, drop relative form for an unambiguous date.
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));
}

/**
 * Human-readable banner copy for a non-`active` notification_state.
 *
 * `rotation_pending` -> no countdown until operator activation.
 * `expired` -> "API key expired N days ago. Rotate now."
 * `notify_*d` -> "API key expires in N days." (or "tomorrow" / "today")
 *
 * Returns `null` for `active` / `null` - caller should not render a banner.
 */
export function formatNotificationState(
  state: NotificationState | null | undefined,
  daysRemaining: number | null | undefined,
): string | null {
  if (!state || state === "active") return null;
  if (state === "rotation_pending") {
    return "New credentials are waiting for activation. Expiry reminders are paused.";
  }
  if (state === "expired") {
    if (daysRemaining == null || daysRemaining === 0) {
      return "API key expires today. Rotate immediately.";
    }
    const overdue = Math.abs(daysRemaining);
    return `API key expired ${overdue} day${overdue === 1 ? "" : "s"} ago. Rotate immediately.`;
  }
  // notify_Nd
  const days = daysRemaining ?? Number(state.replace(/^notify_/, "").replace(/d$/, ""));
  if (days === 1) return "API key expires tomorrow. Rotate soon.";
  if (days === 0) return "API key expires today. Rotate immediately.";
  return `API key expires in ${days} days. Rotate soon.`;
}

/** Severity class suffix for a notification_state, used to color the banner. */
export function notificationSeverityClass(state: NotificationState | null | undefined): string {
  if (state === "expired") return "expiry-banner-expired";
  if (state === "notify_1d") return "expiry-banner-urgent";
  if (state === "notify_2d" || state === "notify_3d") return "expiry-banner-warn";
  return "expiry-banner-info";
}

/** Human-readable label for a RotationCycle dropdown option. */
const ROTATION_CYCLE_LABELS: Record<RotationCycle, string> = {
  weekly: "Weekly (every 7 days)",
  monthly: "Monthly (every month)",
  quarterly: "Quarterly (every 3 months)",
  semi_annual: "Semi-annual (every 6 months)",
  annual: "Annual (every 12 months)",
  custom: "Custom (set in days)",
};

export function formatRotationCycle(cycle: RotationCycle): string {
  return ROTATION_CYCLE_LABELS[cycle] ?? cycle;
}

export const ROTATION_CYCLE_OPTIONS: ReadonlyArray<RotationCycle> = [
  "weekly",
  "monthly",
  "quarterly",
  "semi_annual",
  "annual",
  "custom",
];
